import React, { useContext } from 'react';

import { AppContext } from 'src/components/app-context';
import { useViewportContext } from 'src/components/viewport/context';

import { BodySmallText, FontColor } from '@prisjakt/ui/text/variants';
import { Clickable } from 'src/components/clickable';
import { ShopLinkCard } from 'src/components/shop-link-card';
import { AddToListButton } from 'src/components/action-buttons/add-to-list';
import Rating from 'src/components/rating';
import { ProductMedia } from './media';

import {
  genericSort,
  SortDirection,
  sortOffersByPrice,
} from 'src/components/price-list/sort-by-price';
import { IconChevronRight } from '@prisjakt/ui/icon/chevron-right';
import { filterOffers } from 'src/components/price-list/filtering';
import { getFiltersConfig } from 'src/components/price-list/filters-config';

import type { Price, PriceList } from 'src/types';
import type { ActiveCampaignByProductId } from 'src/hooks/active-campaign-by-product-id';
import { FilterType, PriceDisplayEnum } from 'src/components/price-list/types';
import { TooltipPlacement } from 'src/components/tooltip-new/types';

import * as S from './styles';

const MAX_VISIBLE_OFFERS = 3;

type Props = {
  className?: string;
  'data-test'?: string;
  id?: number;
  imageSrc?: string | null;
  imageSize?: number;
  name?: string;
  link?: string;
  rank?: number | null;
  reviewsCount?: number | null;
  rating?: number | null;
  prices?: Omit<PriceList, 'meta' | 'highlights'> | null;
  campaignProducts?: ActiveCampaignByProductId;
  onClick: () => void;
};

const activateFilters = () => {
  const defaultFilters = getFiltersConfig();
  return defaultFilters.map(filter => {
    const newFilter = { ...filter };
    if (newFilter.type === FilterType.CONDITION) {
      newFilter.filterAlternativeOffers = false;
    }
    return newFilter;
  });
};

export const ExpertOfferListCard = (props: Props) => {
  const { t } = useContext(AppContext);
  const { isMobile } = useViewportContext();
  const {
    className,
    'data-test': dataTest,
    id,
    imageSrc,
    imageSize = 140,
    name,
    link,
    reviewsCount,
    rating,
    prices,
    campaignProducts,
    onClick,
  } = props;

  const filters = activateFilters();

  const filteredOffers = filterOffers(filters, prices);

  const getSortedOffers = () => {
    // Should include offers with unfeatured shops, but sort them last.
    const firstSort = sortOffersByPrice(PriceDisplayEnum.PRICE, filteredOffers);
    const sortByFeaturedShop = genericSort(firstSort, {
      resolver: (offer: Price) => offer.store?.featured || 0,
      sortDirection: SortDirection.Descending,
    });
    return sortByFeaturedShop;
  };

  const sortedOffers = getSortedOffers();
  const slicedPrices = sortedOffers.slice(0, MAX_VISIBLE_OFFERS);
  const campaigns = campaignProducts ? Object.values(campaignProducts) : [];

  return (
    <S.Card
      className={className}
      containerProps={{ 'data-test': `${dataTest}`, onClick }}
    >
      <S.IconWrapper>
        {id && name && (
          <AddToListButton
            dropdownGrow={isMobile ? 'left' : 'right'}
            productId={id}
            productName={name}
            tooltipPlacement={
              isMobile ? TooltipPlacement.LEFT : TooltipPlacement.RIGHT
            }
          />
        )}
      </S.IconWrapper>
      <S.Main>
        <S.ColumnWrapper
          container={Clickable}
          containerProps={{ to: link }}
          style={{ flexDirection: 'column', alignItems: 'center' }}
        >
          <S.ImageWrapper $size={`${imageSize}px`}>
            <ProductMedia
              imageSrc={imageSrc}
              imageSize={imageSize}
              lazyLoadImage
              name={name || ''}
            />
          </S.ImageWrapper>
          {rating && rating > 0 ? (
            <S.RatingWrapper>
              <Rating
                rate={rating}
                count={reviewsCount}
                showCounter
                showCounterAs="number"
                showRate
                showRateAs="number"
              />
            </S.RatingWrapper>
          ) : null}
        </S.ColumnWrapper>
        <S.StoresWrapper>
          {prices && slicedPrices && slicedPrices.length ? (
            slicedPrices.map(
              data =>
                id &&
                data?.id && (
                  <ShopLinkCard
                    key={data.store?.id}
                    data-test="ExpertStoreCard"
                    id={data.store?.id}
                    name={data.store?.name}
                    logoUrl={data.store?.logo}
                    pathname={data.externalUri || ''}
                    hasLogo={data.store?.hasLogo}
                    featured={data.store?.featured}
                    logoWidth={176}
                    rating={data.store?.userReviewSummary.rating}
                    reviewCount={data.store?.userReviewSummary.count}
                    renderMode="native"
                    price={data.price?.exclShipping}
                    stockStatus={data.stock?.status}
                    numericRating
                    productId={id}
                    priceId={data.id}
                    isDeal={
                      campaignProducts
                        ? campaigns.some(
                            campaign => campaign?.priceId === Number(data.id)
                          )
                        : false
                    }
                  />
                )
            )
          ) : (
            <S.MissingPlaceHolder>
              <BodySmallText>
                {t('expert', 'offers.missing-retailers')}
              </BodySmallText>
            </S.MissingPlaceHolder>
          )}
          <S.ActionArea container={Clickable} containerProps={{ to: link }}>
            <S.CTAText>
              <BodySmallText
                color={FontColor.PRIMARY}
                containerProps={{ 'data-test': `${dataTest}-cta` }}
              >
                {slicedPrices.length
                  ? t('category', 'product-offer.cta', {
                      n: sortedOffers.length,
                    })
                  : t('expert', 'offers.view-product-page')}
              </BodySmallText>
              <IconChevronRight color="maybeBlue700" />
            </S.CTAText>
          </S.ActionArea>
        </S.StoresWrapper>
      </S.Main>
    </S.Card>
  );
};
